import { KTSelectConfigInterface, KTSelectOption } from "./config";

/**
 * Theme configuration for KTSelect component
 * Controls visual appearance through CSS variables, allowing easy customization
 */
export interface KTSelectTheme {
	// Base container
	wrapper: string;
	// Display (trigger) element
	display: string;
	displayActive: string;
	displayDisabled: string;
	// Dropdown
	dropdown: string;
	dropdownOpen: string;
	// Option elements
	option: string;
	optionSelected: string;
	optionDisabled: string;
	optionHighlighted: string;
	// Option group
	optionGroup: string;
	optionGroupHeader: string;
	// Search input
	search: string;
	searchWrapper: string;
	searchHighlight: string;
	// Multi-select
	tag: string;
	tagRemove: string;
	// States
	loading: string;
	noResults: string;
}

/**
 * Default theme using Tailwind-compatible classes
 */
export const defaultTheme: KTSelectTheme = {
	// Base container
	wrapper: "relative",
	// Display (trigger) element
	display: "flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400",
	displayActive: "border-blue-400 ring-2 ring-blue-200",
	displayDisabled: "bg-gray-100 opacity-70 cursor-not-allowed",
	// Dropdown
	dropdown: "absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto",
	dropdownOpen: "block",
	// Option elements
	option: "px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center",
	optionSelected: "bg-blue-50 font-medium",
	optionDisabled: "opacity-50 cursor-not-allowed",
	optionHighlighted: "bg-gray-100",
	// Option group
	optionGroup: "py-1",
	optionGroupHeader: "px-3 py-1 text-xs font-semibold text-gray-500 uppercase",
	// Search input
	searchWrapper: "px-3 py-2 border-b border-gray-200",
	search: "w-full border-none focus:outline-none text-sm",
	searchHighlight: "bg-yellow-100 font-medium",
	// Multi-select
	tag: "inline-flex items-center bg-blue-50 border border-blue-100 rounded px-2 py-1 text-sm mr-1 mb-1",
	tagRemove: "ml-1 text-blue-400 hover:text-blue-600 cursor-pointer",
	// States
	loading: "px-3 py-2 text-gray-500 italic",
	noResults: "px-3 py-2 text-gray-500"
};

/**
 * Template interface for KTSelect component
 * Each method returns an HTML string or HTMLElement
 */
export interface KTSelectTemplateInterface {
	// Main components
	main: (config: KTSelectConfigInterface, theme: KTSelectTheme) => HTMLElement;
	display: (config: KTSelectConfigInterface, theme: KTSelectTheme) => HTMLElement;
	dropdown: (config: KTSelectConfigInterface, theme: KTSelectTheme, optionsHtml: string) => HTMLElement;

	// Option rendering
	option: (option: KTSelectOption | HTMLOptionElement, config: KTSelectConfigInterface, theme: KTSelectTheme) => HTMLElement;
	optionGroup: (label: string, optionsHtml: string, config: KTSelectConfigInterface, theme: KTSelectTheme) => HTMLElement;

	// Search and empty states
	search: (config: KTSelectConfigInterface, theme: KTSelectTheme) => HTMLElement;
	noResults: (config: KTSelectConfigInterface, theme: KTSelectTheme) => HTMLElement;
	loading: (config: KTSelectConfigInterface, theme: KTSelectTheme) => HTMLElement;

	// Multi-select
	tag: (option: KTSelectOption, config: KTSelectConfigInterface, theme: KTSelectTheme) => HTMLElement;
	selectedDisplay: (selectedOptions: KTSelectOption[], config: KTSelectConfigInterface, theme: KTSelectTheme) => string;

	// Utility
	highlightSearchTerm: (element: HTMLElement, query: string, theme: KTSelectTheme) => HTMLElement;
}

/**
 * Default templates for KTSelect component
 */
export const defaultTemplates: KTSelectTemplateInterface = {
	/**
	 * Renders the main container for the select component
	 */
	main: (config: KTSelectConfigInterface, theme: KTSelectTheme): HTMLElement => {
		return _html`<div data-kt-select-wrapper class="${theme.wrapper}" data-kt-select-mode="${config.mode || 'default'}"></div>`;
	},

	/**
	 * Renders the display element (trigger) for the select
	 */
	display: (config: KTSelectConfigInterface, theme: KTSelectTheme): HTMLElement => {
		const isCombobox = config.mode === 'combobox';

		if (isCombobox) {
			return _html`
				<div class="relative flex items-center w-full">
					<input
						data-kt-select-search
						data-kt-select-display
						data-kt-select-value
						type="text"
						class="${theme.display}"
						placeholder="${config.placeholder || 'Select...'}"
						role="searchbox"
						aria-label="${config.label || config.placeholder || 'Select...'}"
						${config.disabled ? 'disabled' : ''}
					/>
					<button
						type="button"
						data-kt-select-clear-button
						class="absolute right-3 hidden text-gray-400 hover:text-gray-600"
						aria-label="Clear selection">
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
							<line x1="18" y1="6" x2="6" y2="18"></line>
							<line x1="6" y1="6" x2="18" y2="18"></line>
						</svg>
					</button>
				</div>
			`;
		}

		return _html`
			<div
				data-kt-select-display
				class="${theme.display}"
				tabindex="${config.disabled ? '-1' : '0'}"
				role="button"
				aria-haspopup="listbox"
				aria-expanded="false"
				aria-label="${config.label || config.placeholder || 'Select...'}"
				${config.disabled ? 'aria-disabled="true"' : ''}
			>
				<span data-kt-select-value>${config.placeholder || 'Select...'}</span>
				<span data-kt-select-arrow class="ml-2">
					<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<polyline points="6 9 12 15 18 9"></polyline>
					</svg>
				</span>
			</div>
		`;
	},

	/**
	 * Renders the dropdown content container
	 */
	dropdown: (config: KTSelectConfigInterface, theme: KTSelectTheme, optionsHtml: string): HTMLElement => {
		const isCombobox = config.mode === 'combobox';
		const hasSearch = config.enableSearch && !isCombobox;

		// First create the dropdown element
		const dropdownElement = document.createElement('div');
		dropdownElement.setAttribute('data-kt-select-dropdown-content', '');
		dropdownElement.className = `${theme.dropdown} hidden`;

		if (config.dropdownZindex) {
			dropdownElement.style.zIndex = config.dropdownZindex.toString();
		}

		// Add search input at the top if needed
		if (hasSearch) {
			// Add the search input directly as the first child
			const searchElement = defaultTemplates.search(config, theme);
			dropdownElement.appendChild(searchElement);
		}

		// Add options container
		const optionsContainer = document.createElement('ul');
		optionsContainer.setAttribute('role', 'listbox');
		optionsContainer.setAttribute('aria-label', config.label || 'Options');
		optionsContainer.setAttribute('data-kt-select-options-container', '');
		optionsContainer.style.maxHeight = `${config.height}px`;
		optionsContainer.style.overflowY = 'auto';
		optionsContainer.innerHTML = optionsHtml;

		// Add options container as the second child (or first if no search)
		dropdownElement.appendChild(optionsContainer);

		return dropdownElement;
	},

	/**
	 * Renders a single option
	 */
	option: (option: KTSelectOption | HTMLOptionElement, config: KTSelectConfigInterface, theme: KTSelectTheme): HTMLElement => {
		// Handle both direct KTSelectOption objects and HTMLOptionElements
		const isHtmlOption = option instanceof HTMLOptionElement;

		const value = isHtmlOption ? option.value : (option as KTSelectOption).id;
		const text = isHtmlOption ? option.text : (option as KTSelectOption).title;
		const disabled = isHtmlOption ? option.disabled : (option as any).disabled === true;
		const selected = isHtmlOption ? option.selected : !!(option as KTSelectOption).selected;

		// Get additional data attributes from option
		const description = isHtmlOption
			? option.getAttribute('data-kt-select-option-description')
			: (option as KTSelectOption).description;

		const icon = isHtmlOption
			? option.getAttribute('data-kt-select-option-icon')
			: (option as KTSelectOption).icon;

		// Process icon with width and height if available
		let iconHtml = '';
		if (icon && icon !== 'null' && icon !== 'undefined') {
			// Check if it's an image URL
			if (icon.includes('http') || icon.includes('.')) {
				const imgElement = document.createElement('img');
				imgElement.src = icon;
				imgElement.alt = "Icon";

				// Apply default dimensions
				imgElement.style.width = '24px';
				imgElement.style.height = '24px';

				// Override with config values if available
				if (config.dataFieldIconWidth) {
					imgElement.style.width = config.dataFieldIconWidth;
				}
				if (config.dataFieldIconHeight) {
					imgElement.style.height = config.dataFieldIconHeight;
				}

				iconHtml = `<span class="option-icon mr-2">${imgElement.outerHTML}</span>`;
			} else {
				// It's HTML content
				iconHtml = `<span class="option-icon mr-2">${icon}</span>`;
			}
		}

		// Build option element with proper accessibility attributes
		return _html`
			<li
				data-kt-select-option
				data-value="${value}"
				class="${theme.option} ${selected ? theme.optionSelected : ''} ${disabled ? theme.optionDisabled : ''}"
				role="option"
				${selected ? 'aria-selected="true"' : 'aria-selected="false"'}
				${disabled ? 'aria-disabled="true"' : ''}
			>
				${iconHtml ? iconHtml : ''}
				<div class="option-content">
					<div class="option-title">${text}</div>
					${description && description !== 'null' && description !== 'undefined' ? `<div class="option-description text-sm text-gray-500">${description}</div>` : ''}
				</div>
			</li>
		`;
	},

	/**
	 * Renders an option group with header
	 */
	optionGroup: (label: string, optionsHtml: string, config: KTSelectConfigInterface, theme: KTSelectTheme): HTMLElement => {
		return _html`
			<li role="group" aria-label="${label}" class="${theme.optionGroup}">
				<div class="${theme.optionGroupHeader}">${label}</div>
				<ul>${optionsHtml}</ul>
			</li>
		`;
	},

	/**
	 * Renders the search input
	 */
	search: (config: KTSelectConfigInterface, theme: KTSelectTheme): HTMLElement => {
		return _html`
			<div class="${theme.searchWrapper}">
				<input
					type="text"
					data-kt-select-search
					placeholder="${config.searchPlaceholder || 'Search...'}"
					class="${theme.search}"
					role="searchbox"
					aria-label="${config.searchPlaceholder || 'Search...'}"
				/>
			</div>
		`;
	},

	/**
	 * Renders the no results message
	 */
	noResults: (config: KTSelectConfigInterface, theme: KTSelectTheme): HTMLElement => {
		return _html`
			<li class="${theme.noResults}" role="status">
				${config.searchNotFoundText || 'No results found'}
			</li>
		`;
	},

	/**
	 * Renders the loading state
	 */
	loading: (config: KTSelectConfigInterface, theme: KTSelectTheme): HTMLElement => {
		return _html`
			<li class="${theme.loading}" role="status" aria-live="polite">
				Loading options...
			</li>
		`;
	},

	/**
	 * Renders a tag for multi-select
	 */
	tag: (option: KTSelectOption, config: KTSelectConfigInterface, theme: KTSelectTheme): HTMLElement => {
		// Escape HTML characters for aria-label to prevent HTML injection
		const escapeHTML = (str: string) => {
			return str.replace(/[&<>"']/g, (match) => {
				const escapeMap: Record<string, string> = {
					'&': '&amp;',
					'<': '&lt;',
					'>': '&gt;',
					'"': '&quot;',
					"'": '&#39;'
				};
				return escapeMap[match];
			});
		};

		// Ensure we have plain text for the aria-label
		const safeTitle = escapeHTML(option.title);

		return _html`
			<div data-kt-select-tag class="${theme.tag}">
				<span>${option.title}</span>
				<span
					data-kt-select-remove-button
					data-value="${option.id}"
					class="${theme.tagRemove}"
					role="button"
					aria-label="Remove ${safeTitle}"
					tabindex="0"
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<line x1="18" y1="6" x2="6" y2="18"></line>
						<line x1="6" y1="6" x2="18" y2="18"></line>
					</svg>
				</span>
			</div>
		`;
	},

	/**
	 * Formats the display of selected values
	 */
	selectedDisplay: (selectedOptions: KTSelectOption[], config: KTSelectConfigInterface, theme: KTSelectTheme): string => {
		if (!selectedOptions || selectedOptions.length === 0) {
			return config.placeholder || 'Select...';
		}

		if (config.multiple) {
			if (config.renderSelected && typeof config.renderSelected === 'function') {
				return config.renderSelected(selectedOptions);
			}

			if (config.showSelectedCount) {
				const count = selectedOptions.length;
				return `${count} ${count === 1 ? 'item' : 'items'} selected`;
			}

			return selectedOptions.map(option => option.title).join(', ');
		} else {
			return selectedOptions[0].title;
		}
	},

	/**
	 * Highlights search term in an option
	 */
	highlightSearchTerm: (element: HTMLElement, query: string, theme: KTSelectTheme): HTMLElement => {
		if (!query || query.trim() === '') {
			return element;
		}

		const regex = new RegExp(`(${query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');

		function highlightTextNodes(node: Node) {
			if (node.nodeType === Node.TEXT_NODE) {
				const text = node.nodeValue || '';
				if (text.trim() === '') return;

				const highlightedText = text.replace(regex, `<span class="${theme.searchHighlight}">$1</span>`);

				if (highlightedText !== text) {
					const tempElement = document.createElement('span');
					tempElement.innerHTML = highlightedText;
					node.parentNode?.replaceChild(tempElement, node);
				}
			} else if (node.nodeType === Node.ELEMENT_NODE) {
				// Skip elements that already have highlighting
				if (node.nodeName === 'SPAN' && (node as HTMLElement).classList.contains(theme.searchHighlight.split(' ')[0])) {
					return;
				}

				// Recursively traverse child nodes
				Array.from(node.childNodes).forEach(highlightTextNodes);
			}
		}

		// Create a clone to avoid modifying the original element
		const clonedElement = element.cloneNode(true) as HTMLElement;

		// Process only the text content part of the option
		const textContainer = clonedElement.querySelector('.option-title') || clonedElement;
		Array.from(textContainer.childNodes).forEach(highlightTextNodes);

		return clonedElement;
	}
};

/**
 * Template registry to store and manage custom templates
 */
export class TemplateRegistry {
	private static _instance: TemplateRegistry;
	private _templates: Map<string, KTSelectTemplateInterface> = new Map();
	private _themes: Map<string, KTSelectTheme> = new Map();

	private constructor() {
		// Initialize with default templates
		this._templates.set('default', defaultTemplates);
		this._themes.set('default', defaultTheme);
	}

	public static getInstance(): TemplateRegistry {
		if (!TemplateRegistry._instance) {
			TemplateRegistry._instance = new TemplateRegistry();
		}
		return TemplateRegistry._instance;
	}

	/**
	 * Register a complete template set
	 */
	public registerTemplates(name: string, templates: Partial<KTSelectTemplateInterface>): void {
		const existing = this.getTemplates('default');
		this._templates.set(name, { ...existing, ...templates });
	}

	/**
	 * Register a single template method
	 */
	public registerTemplate(templateSet: string, templateName: keyof KTSelectTemplateInterface, template: any): void {
		const templates = this.getTemplates(templateSet);
		this._templates.set(templateSet, { ...templates, [templateName]: template });
	}

	/**
	 * Register a theme
	 */
	public registerTheme(name: string, theme: Partial<KTSelectTheme>): void {
		const existing = this.getTheme('default');
		this._themes.set(name, { ...existing, ...theme });
	}

	/**
	 * Get a complete template set
	 */
	public getTemplates(name: string = 'default'): KTSelectTemplateInterface {
		return this._templates.get(name) || defaultTemplates;
	}

	/**
	 * Get a theme
	 */
	public getTheme(name: string = 'default'): KTSelectTheme {
		return this._themes.get(name) || defaultTheme;
	}
}

/**
 * Helper function to create HTML elements from template strings
 */
export function _html(strings: TemplateStringsArray, ...values: any[]): HTMLElement {
	const template = document.createElement('template');
	template.innerHTML = strings.reduce((acc, str, i) => acc + str + (values[i] || ''), '').trim();
	return template.content.firstElementChild as HTMLElement;
}

// Export the templates registry for ease of use
export const Templates = TemplateRegistry.getInstance();